
import { useState, useEffect } from 'react';

interface GoogleReview {
  author_name: string;
  author_url?: string;
  language: string;
  profile_photo_url: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
}

interface GooglePlaceData {
  reviews: GoogleReview[];
  rating: number;
  user_ratings_total: number;
  place_name?: string;
}

export default function TestimonialsSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [googleReviews, setGoogleReviews] = useState<GoogleReview[]>([]);
  const [placeData, setPlaceData] = useState<GooglePlaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Fallback testimonials in case Google reviews fail to load
  const fallbackTestimonials = [
    {
      author_name: 'Priya Sharma',
      rating: 5,
      text: 'Excellent service! Dr. Sandhya and her team made my root canal treatment completely painless. The clinic is very clean and modern. Highly recommended for anyone looking for quality dental care in Chennai.',
      relative_time_description: '2 weeks ago',
      profile_photo_url: 'https://readdy.ai/api/search-image?query=Happy%20Indian%20woman%20patient%20smiling%20after%20dental%20treatment%2C%20professional%20portrait%2C%20clean%20background%2C%20satisfied%20customer%2C%20dental%20clinic%20setting&width=100&height=100&seq=patient-1&orientation=squarish'
    },
    {
      author_name: 'Rajesh Kumar',
      rating: 5,
      text: 'Got my dental implants done here. The entire process was smooth and professional. The staff is very caring and the results are amazing! Worth every penny spent.',
      relative_time_description: '1 month ago',
      profile_photo_url: 'https://readdy.ai/api/search-image?query=Middle-aged%20Indian%20man%20smiling%20confidently%2C%20professional%20headshot%2C%20clean%20background%2C%20satisfied%20dental%20patient%2C%20business%20casual%20attire&width=100&height=100&seq=patient-2&orientation=squarish'
    },
    {
      author_name: 'Meera Patel',
      rating: 5,
      text: 'My daughter loves coming here for her dental check-ups. The pediatric dentist is wonderful with children. Great experience every time! The clinic environment is very child-friendly.',
      relative_time_description: '3 weeks ago',
      profile_photo_url: 'https://readdy.ai/api/search-image?query=Indian%20mother%20with%20young%20daughter%2C%20both%20smiling%20happily%2C%20family%20portrait%2C%20clean%20background%2C%20dental%20clinic%20setting%2C%20satisfied%20patients&width=100&height=100&seq=patient-3&orientation=squarish'
    },
    {
      author_name: 'Arjun Reddy',
      rating: 5,
      text: 'Best dental clinic in Thiruverkadu! Professional staff, modern equipment, and affordable prices. My teeth whitening results exceeded my expectations.',
      relative_time_description: '1 week ago',
      profile_photo_url: 'https://readdy.ai/api/search-image?query=Young%20Indian%20professional%20man%20with%20bright%20white%20smile%2C%20confident%20expression%2C%20clean%20background%2C%20dental%20treatment%20success%20story&width=100&height=100&seq=patient-4&orientation=squarish'
    },
    {
      author_name: 'Lakshmi Iyer',
      rating: 5,
      text: 'Fantastic experience with my orthodontic treatment. The braces were fitted perfectly and the follow-up care was excellent. Highly recommend this clinic!',
      relative_time_description: '2 months ago',
      profile_photo_url: 'https://readdy.ai/api/search-image?query=Indian%20woman%20with%20beautiful%20straight%20teeth%2C%20orthodontic%20treatment%20success%2C%20professional%20portrait%2C%20clean%20background%2C%20satisfied%20patient&width=100&height=100&seq=patient-5&orientation=squarish'
    },
    {
      author_name: 'Vikram Singh',
      rating: 4,
      text: 'Good service and clean facilities. The dentist explained everything clearly before the procedure. Only minor wait time, but overall satisfied with the treatment.',
      relative_time_description: '5 days ago',
      profile_photo_url: 'https://readdy.ai/api/search-image?query=Indian%20businessman%20smiling%2C%20professional%20headshot%2C%20clean%20background%2C%20satisfied%20dental%20patient%2C%20formal%20attire&width=100&height=100&seq=patient-6&orientation=squarish'
    }
  ];

  useEffect(() => {
    fetchGoogleReviews();
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (!autoScroll) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const reviews = googleReviews.length > 0 ? googleReviews : fallbackTestimonials;
        return (prev + 1) % reviews.length;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [googleReviews, autoScroll]);

  const fetchGoogleReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call our Supabase Edge Function to fetch Google reviews
      const response = await fetch('https://frfzuscybgpcaphkvqfo.supabase.co/functions/v1/google-reviews', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.reviews && data.reviews.length > 0) {
        setGoogleReviews(data.reviews);
        setPlaceData({
          reviews: data.reviews,
          rating: data.rating,
          user_ratings_total: data.user_ratings_total,
          place_name: data.place_name
        });
        setError(null);
      } else if (data.fallback) {
        // API key not configured or other issue, use fallback
        setError(data.error || 'Google Reviews API not configured');
      } else {
        setError('No reviews available');
      }
    } catch (err) {
      console.warn('Google Reviews API unavailable, using fallback testimonials:', err);
      setError('Using sample testimonials');
    } finally {
      setLoading(false);
    }
  };

  // Use Google reviews if available, otherwise use fallback
  const displayReviews = googleReviews.length > 0 ? googleReviews : fallbackTestimonials;
  const isGoogleReviews = googleReviews.length > 0;

  const nextSlide = () => {
    setAutoScroll(false);
    setCurrentSlide((prev) => (prev + 1) % displayReviews.length);
    setTimeout(() => setAutoScroll(true), 10000); // Resume auto-scroll after 10s
  };

  const prevSlide = () => {
    setAutoScroll(false);
    setCurrentSlide((prev) => (prev - 1 + displayReviews.length) % displayReviews.length);
    setTimeout(() => setAutoScroll(true), 10000); // Resume auto-scroll after 10s
  };

  const goToSlide = (index: number) => {
    setAutoScroll(false);
    setCurrentSlide(index);
    setTimeout(() => setAutoScroll(true), 10000); // Resume auto-scroll after 10s
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <i 
        key={i} 
        className={`${i < rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'} text-lg`}
      />
    ));
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="flex items-center justify-center mb-4">
            <i className="ri-star-fill text-yellow-400 text-2xl sm:text-3xl mr-2"></i>
            <span className="text-[#012B53] font-bold text-base sm:text-lg uppercase tracking-wide">
              What Our Patients Say
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {isGoogleReviews ? 'Google Reviews from Happy Smiles' : 'Patient Testimonials'}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8">
            {isGoogleReviews 
              ? 'Live reviews from our patients on Google, automatically updated when new reviews are posted.'
              : 'Don\'t just take our word for it. Here\'s what our satisfied patients have to say about their experience at The Tooth Doctor.'
            }
          </p>
          
          {loading && (
            <div className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-[#012B53] text-xl mr-2"></i>
              <span className="text-[#012B53] font-medium">Loading reviews...</span>
            </div>
          )}
          
          {error && !loading && !isGoogleReviews && (
            <div className="inline-flex items-center px-4 py-3 bg-blue-100 border-l-4 border-blue-400 text-blue-700 rounded-r-lg">
              <i className="ri-information-line mr-2"></i>
              Showing patient testimonials.
            </div>
          )}
        </div>

        {/* Reviews Carousel */}
        <div className="relative max-w-6xl mx-auto">
          <div className="overflow-hidden rounded-2xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {displayReviews.map((review, index) => (
                <div key={index} className="w-full flex-shrink-0 px-2 sm:px-4">
                  <div className="bg-white rounded-2xl shadow-xl border-2 border-[#D4AF37]/20 p-6 sm:p-8 md:p-12 mx-auto max-w-4xl">
                    {/* Google Badge */}
                    {isGoogleReviews && (
                      <div className="flex items-center justify-center mb-4 sm:mb-6">
                        <div className="flex items-center bg-blue-50 px-3 sm:px-4 py-2 rounded-full">
                          <img 
                            src="https://readdy.ai/api/search-image?query=Google%20logo%2C%20official%20Google%20brand%20icon%2C%20clean%20white%20background%2C%20high%20quality%20logo&width=24&height=24&seq=google-badge&orientation=squarish" 
                            alt="Google"
                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                          />
                          <span className="text-xs sm:text-sm font-medium text-gray-600">Google Review</span>
                          <div className="ml-2 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                    )}
                    
                    {/* Rating */}
                    <div className="flex justify-center mb-4 sm:mb-6">
                      <div className="flex items-center space-x-1">
                        {renderStars(review.rating)}
                        <span className="ml-2 text-xl sm:text-2xl font-bold text-[#012B53]">
                          {review.rating}.0
                        </span>
                      </div>
                    </div>
                    
                    {/* Review Text */}
                    <blockquote className="text-lg sm:text-xl md:text-2xl text-gray-700 mb-6 sm:mb-8 leading-relaxed text-center font-medium">
                      "{truncateText(review.text, 200)}"
                    </blockquote>
                    
                    {/* Reviewer Info */}
                    <div className="flex items-center justify-center space-x-3 sm:space-x-4">
                      <div className="relative">
                        {review.profile_photo_url ? (
                          <img 
                            src={review.profile_photo_url} 
                            alt={review.author_name}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 sm:border-3 border-[#D4AF37]"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#012B53] flex items-center justify-center text-white font-bold text-sm sm:text-lg border-2 sm:border-3 border-[#D4AF37] ${review.profile_photo_url ? 'hidden' : ''}`}>
                          {getInitials(review.author_name)}
                        </div>
                      </div>
                      <div className="text-left">
                        <h4 className="font-bold text-gray-900 text-lg sm:text-xl">{review.author_name}</h4>
                        <p className="text-[#D4AF37] font-semibold text-sm sm:text-base">
                          {review.relative_time_description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button 
            onClick={prevSlide}
            className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 bg-white rounded-full shadow-xl border-2 border-[#D4AF37] flex items-center justify-center text-[#012B53] hover:bg-[#012B53] hover:text-white transition-all duration-300 cursor-pointer z-10"
          >
            <i className="ri-arrow-left-line text-lg sm:text-2xl"></i>
          </button>
          
          <button 
            onClick={nextSlide}
            className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 bg-white rounded-full shadow-xl border-2 border-[#D4AF37] flex items-center justify-center text-[#012B53] hover:bg-[#012B53] hover:text-white transition-all duration-300 cursor-pointer z-10"
          >
            <i className="ri-arrow-right-line text-lg sm:text-2xl"></i>
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center space-x-2 sm:space-x-3 mt-6 sm:mt-8">
          {displayReviews.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentSlide 
                  ? 'bg-[#D4AF37] scale-125' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        {/* Reviews Stats & CTA */}
        <div className="mt-12 sm:mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 bg-white rounded-2xl shadow-xl border-2 border-[#D4AF37]/30 p-6 sm:p-8">
            <div className="flex items-center space-x-3">
              <img 
                src="https://readdy.ai/api/search-image?query=Google%20logo%2C%20official%20Google%20brand%20icon%2C%20clean%20white%20background%2C%20high%20quality%20logo&width=48&height=48&seq=google-main&orientation=squarish" 
                alt="Google"
                className="w-10 h-10 sm:w-12 sm:h-12"
              />
              <div className="text-left">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-2xl sm:text-3xl font-bold text-[#012B53]">
                    {placeData ? placeData.rating.toFixed(1) : '4.9'}
                  </span>
                  <div className="flex">
                    {renderStars(5)}
                  </div>
                </div>
                <p className="text-gray-600 font-medium text-sm sm:text-base">
                  Based on {placeData ? placeData.user_ratings_total : '200+'} {isGoogleReviews ? 'Google Reviews' : 'Patient Reviews'}
                  {isGoogleReviews && (
                    <span className="ml-2 text-green-600 text-xs sm:text-sm font-semibold">
                      <i className="ri-refresh-line mr-1"></i>Live Updates
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href="https://g.page/r/CcYHLknWVrNrEAE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 sm:px-6 py-3 bg-[#012B53] text-white rounded-xl hover:bg-[#001f3d] transition-colors font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center text-sm sm:text-base"
              >
                <i className="ri-star-line mr-2"></i>
                Write a Review
              </a>
              
              <a
                href="https://share.google/rxiCsRIdmp1TFSLxD"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 sm:px-6 py-3 bg-[#D4AF37] text-[#012B53] rounded-xl hover:bg-[#c19b2b] transition-colors font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center text-sm sm:text-base"
              >
                <i className="ri-external-link-line mr-2"></i>
                View All Reviews
              </a>
            </div>
          </div>
        </div>

        {/* Status Message */}
        <div className="mt-6 sm:mt-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-medium">
            <i className="ri-heart-line mr-2"></i>
            {isGoogleReviews ? 'Live Google Reviews' : 'Authentic Patient Testimonials'}
          </div>
        </div>
      </div>
    </section>
  );
}
